import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Loader2, Wifi } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';
// import { runAllNetworkTests, isRunningInSimulator } from '@/utils/api-test';

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // const [isTestingApi, setIsTestingApi] = useState(false);
  // const [testResults, setTestResults] = useState<any>(null);
  // const isSimulator = isRunningInSimulator();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    // Check if we have a direct session already
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (data.session && !error) {
        logger.log("Found existing session:", data.session.user.id);
        navigate('/app');
      }
    };

    checkSession();
  }, [navigate]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      
      logger.log("Login response:", { data, error });
      
      if (error) {
        logger.error("Login error details:", error);
        setErrorMessage(error.message || "Authentication failed. Check your credentials and try again.");
        toast({
          title: "Login failed",
          description: error.message || "Check your credentials and try again",
          variant: "destructive",
        });
        return;
      }
      
      if (data?.user) {
        logger.log("Login successful, user:", data.user.id);

        // Navigate to app after login
        navigate('/app');
      } else {
        logger.error("No user data returned from successful login");
        setErrorMessage("Authentication succeeded but user data is missing");
        toast({
          title: "Login issue",
          description: "Authentication succeeded but user data is missing",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      logger.error("Unexpected login error:", error);
      setErrorMessage(error?.message || "An unexpected error occurred");
      toast({
        title: "Login failed",
        description: error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full p-6 bg-card rounded-lg shadow-lg border">
        <div className="text-center mb-6">
        <LogIn className="mx-auto h-10 w-10 text-primary" />
        <h1 className="text-2xl font-bold mt-2">Welcome Back</h1>
        <p className="text-muted-foreground mt-1">Sign in to your account to continue</p>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded border border-destructive">
          <p className="text-sm">{errorMessage}</p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="you@example.com" {...field} type="email" autoComplete="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="••••••••" {...field} autoComplete="current-password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </Form>
      
      
      <div className="mt-6 text-center text-sm">
        <p className="text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="text-primary font-medium hover:underline">
            Sign up
          </Link>
        </p>
      </div>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          <p>
            If you're having trouble logging in, make sure you've verified your email address.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
